import React, { useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, FileText, Users, BookOpen, Calendar, Clock, BarChart3, List } from 'lucide-react';
import Layout from '../components/Layout';
import api from '../components/api';
import { formatDate, formatTime } from '../utils/formatUtils';
import { useExamenStats } from '../hooks/useExamenStats';

// Lazy loading des composants lourds
const ListeNotes = lazy(() => import('../components/examen/ListeNotes'));
const StatistiquesTab = lazy(() => import('../components/examen/StatistiquesTab'));

// Composant de chargement pour le lazy loading
const LoadingSpinner = () => (
  <div className="flex justify-center items-center h-32">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#202654]"></div>
    <span className="ml-3 text-gray-600">Chargement...</span>
  </div>
);

const ListeNoteExamenPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  
  const [session, setSession] = useState(null);
  const [examen, setExamen] = useState(null);
  const [allNotes, setAllNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('liste');

  // Utiliser le hook optimisé pour les statistiques
  const stats = useExamenStats(allNotes);

  useEffect(() => {
    loadNotesData();
  }, [sessionId]);

  const loadNotesData = useCallback(async () => {
    try {
      setLoading(true);
      console.time('⏱️ Chargement des données');
      
      const [sessionResponse, notesResponse] = await Promise.all([
        api.get(`/examen/sessions/${sessionId}/`),
        api.get(`/examen/sessions/${sessionId}/notes/`)
      ]);
      
      const sessionData = sessionResponse.data.data || sessionResponse.data;
      const notesData = notesResponse.data.data || notesResponse.data;
      
      console.log(`📊 ${notesData.length} notes chargées`);
      
      setSession(sessionData);
      setAllNotes(notesData);
      
      // Charger l'examen seulement si nécessaire
      const examenId = sessionData.examen?.id || sessionData.examen;
      if (examenId) {
        const examenResponse = await api.get(`/examen/examens/${examenId}/`);
        const examenData = examenResponse.data.data || examenResponse.data;
        setExamen(examenData);
      }
      
      console.timeEnd('⏱️ Chargement des données');
    } catch (error) {
      console.error('❌ Erreur lors du chargement:', error);
      if (error.response?.status === 404) {
        toast.error('Session d\'examen ou notes introuvables');
        navigate('/examens');
      } else {
        toast.error('Impossible de charger les données');
      }
    } finally {
      setLoading(false);
    }
  }, [sessionId, navigate]);

  // Mémoriser le composant CustomTooltip pour éviter les re-rendus
  const CustomTooltip = useMemo(() => ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">{label}</p>
          {payload.map((entry, index) => {
            let value = entry.value;
            let unit = '';
            
            // Déterminer l'unité selon le nom du champ
            if (entry.dataKey === 'moyenne') {
              unit = '/20';
            } else if (entry.dataKey === 'tauxReussite') {
              unit = '%';
            } else if (entry.dataKey === 'pourcentage') {
              unit = '%';
            } else if (entry.name && entry.name.includes('%')) {
              unit = '%';
            } else if (entry.name && entry.name.includes('/20')) {
              unit = '/20';
            } else {
              unit = entry.name === 'pourcentage' ? '%' : ' étudiants';
            }
            
            return (
              <p key={index} style={{ color: entry.color }} className="text-sm">
                {entry.name}: {value}{unit}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  }, []);

  // Mémoriser les handlers pour éviter les re-rendus
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
  }, []);

  const handleBackNavigation = useCallback(() => {
    navigate(`/examens/${examen?.id || session?.examen?.id || session?.examen}`);
  }, [navigate, examen, session]);

  // Mémoriser les informations d'en-tête pour éviter les recalculs
  const headerInfo = useMemo(() => ({
    examenLibelle: examen?.libelle || 'Examen',
    moduleLibelle: session?.module?.libelle || session?.module?.module?.libelle || 'Module',
    classeLibelle: examen?.classe?.libelle || session?.examen?.classe?.libelle || 'Classe',
    dateExamen: session?.date_examen ? formatDate(session.date_examen) : null,
    heureExamen: session?.heure_debut && session?.heure_fin 
      ? `${formatTime(session.heure_debut)} - ${formatTime(session.heure_fin)}` 
      : null
  }), [examen, session]);

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
            <span className="ml-3 text-gray-600">Chargement des notes...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        {/* En-tête */}
        <div className="mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={handleBackNavigation}
                className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Retour aux détails de l'examen
              </button>
            </div>
            
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Résultats de la session d'examen
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center">
                  <FileText className="h-4 w-4 mr-1" />
                  {headerInfo.examenLibelle}
                </div>
                <div className="flex items-center">
                  <BookOpen className="h-4 w-4 mr-1" />
                  {headerInfo.moduleLibelle}
                </div>
                <div className="flex items-center">
                  <Users className="h-4 w-4 mr-1" />
                  {headerInfo.classeLibelle}
                </div>
                {headerInfo.dateExamen && (
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    {headerInfo.dateExamen}
                  </div>
                )}
                {headerInfo.heureExamen && (
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    {headerInfo.heureExamen}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Onglets */}
        <div className="mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => handleTabChange('liste')}
                className={`flex items-center px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'liste'
                    ? 'bg-[#202654] text-white border-b-2 border-[#202654]'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <List className="h-4 w-4 mr-2" />
                Liste des notes ({allNotes.length})
              </button>
              <button
                onClick={() => handleTabChange('statistiques')}
                className={`flex items-center px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'statistiques'
                    ? 'bg-[#202654] text-white border-b-2 border-[#202654]'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                Statistiques & Graphiques
              </button>
            </div>
          </div>
        </div>

        {/* Contenu des onglets avec lazy loading */}
        <Suspense fallback={<LoadingSpinner />}>
          {activeTab === 'liste' && (
            <ListeNotes 
              allNotes={allNotes} 
              searchTerm={searchTerm} 
              setSearchTerm={setSearchTerm}
              session={session}
              examen={examen}
            />
          )}

          {activeTab === 'statistiques' && stats && (
            <StatistiquesTab stats={stats} CustomTooltip={CustomTooltip} />
          )}
        </Suspense>
      </div>
    </Layout>
  );
};

export default ListeNoteExamenPage; 